"use client";
import { useState, useEffect, useRef, ChangeEvent } from "react";
import Modal from "react-bootstrap/Modal";
import Datagrid from "../../components/Datagrid/Datagrid";
import { MdOutlineCalendarMonth } from "react-icons/md";
import ModalIconTitle from "../../components//ModalIconTitle";
import common from "../../utils/common";
import type toast from "react-hot-toast";
import CalendarBackend from "../../components/Calendar/utils/CalendarBackend";
import { cal_calendar } from "../../class/prisma/db";
import { AgGridReact } from "ag-grid-react";
import { SelectionChangedEvent } from "ag-grid-community";
import CreateEditDeleteButtonGroup from "../../components/CreateEditDeleteButtonGroup";
import SearchInput from "../../components/Datagrid/SearchInput";
import CalendarModal from "./CalendarModal";
import { SystemMsg } from "../../utils/SystemMsg";
import { ColorColumnCell } from "../../components/ColorColumnCell";
import { setCalendars } from "../../store/features/cacheSlice";
import { useDispatch } from "react-redux";
import { CalendarViewObject } from "../../class/calendar.schema";
import { useI18n } from "../../i18n";

type CalendarListModalProps = {
  show: boolean;
  onClose: () => void;
  toast: typeof toast;
};

function CalendarListModal({ show, onClose, toast }: CalendarListModalProps) {
  const { t } = useI18n();
  const title = t("calendar.listTitle");
  const [rowData, setRowData] = useState<any[]>();
  const [gridLoading, setGridLoading] = useState<boolean>(true);
  const gridRef = useRef<AgGridReact>(null);

  const colorColumnRenderer = (params: any) => {
    const isHex = /^#([0-9A-F]{3}){1,2}$/i.test(params.value);
    const colorText = isHex
      ? params.value.toString().toUpperCase()
      : params.value.toString();
    return <ColorColumnCell color={params.value} text={colorText} />;
  };

  const colDefs = [
    { field: "id", width: 50 },
    { field: "name", headerName: "Calendar", flex: 2 },
    { field: "color", flex: 1, cellRenderer: colorColumnRenderer },
  ];

  const dispatch = useDispatch();
  const [initialized, setInitialized] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageCount, setPageCount] = useState(0);
  const [paginationPage, setPaginationPage] = useState(0);

  const [selectedId, setSelectedId] = useState(-1);
  const [crudMode, setCrudMode] = useState<"add" | "edit">("add");
  const [showModal, setShowModal] = useState(false);
  const [modalDataObj, setModalDataObj] = useState<cal_calendar | null>(null);

  const [keyword, setKeyword] = useState("");

  const [modalClassName, setModalClassName] = useState(" ");

  const getRowData = (updateGrid = true) => {
    setGridLoading(true);
    CalendarBackend.getCalendarData(
      (data) => {
        let calendarData = data.data;
        if (!calendarData || !Array.isArray(calendarData)) {
          return;
        }
        if (updateGrid) {
          const total =
            data.total != null && !Number.isNaN(data.total.toString())
              ? data.total
              : 0;
          setPageCount(common.getPageCount(total, pageSize));
          setRowData([...calendarData]);          
        }
        dispatch(setCalendars(calendarData));
        setGridLoading(false);
      },
      keyword,
      page,
      pageSize,
    );
  };

  useEffect(() => {
    if (!show) {
      return;
    }

    if (!initialized) {
      getRowData();
      setInitialized(true);
      return;
    }

    const debounce = setTimeout(() => {
      if (initialized) {
        getRowData();
      }
    }, common.DEBOUNCE_TIME);
    return () => clearTimeout(debounce);
  }, [show, keyword, page, pageSize]);

  useEffect(() => {
    setPage(1);
    setPaginationPage(0);
  }, [pageSize]);

  useEffect(() => {
    blurCurrentModal(showModal);
  }, [showModal]);

  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();
    }

    gridRef.current?.api?.deselectAll();
    setRowData([]);
    setGridLoading(true);
    setPaginationPage(0);
    setPage(1);
    setSelectedId(-1);
    setInitialized(false);
  };

  const onSelectionChanged = (event: SelectionChangedEvent) => {
    if (
      !event ||
      !event.selectedNodes ||
      event.selectedNodes === null ||
      !Array.isArray(event.selectedNodes) ||
      event.selectedNodes.length === 0
    ) {
      setSelectedId(-1);
      return;
    }
    const selectedNode = event.selectedNodes[0];
    setSelectedId(selectedNode.data.id);
  };

  const handlePageClick = (event: { selected: number }) => {
    if (!event || event?.selected == null || Number.isNaN(event.selected)) {
      return;
    }
    setPage(event.selected + 1);
    setPaginationPage(event.selected);
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (
      !event ||
      !event?.target ||
      event?.target?.value == null ||
      Number.isNaN(event?.target?.value)
    ) {
      return;
    }
    setPageSize(Number(event.target.value));
  };

  const onCreateButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setCrudMode("add");
    setShowModal(true);
    setModalDataObj(null);
  };

  const onEditButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!rowData) return;
    setCrudMode("edit");
    setShowModal(true);
    const dataObj = rowData?.find((x) => x.id === selectedId) || null;
    setModalDataObj(dataObj);
  };

  const blurCurrentModal = (blur: boolean) => {
    if (blur) {
      setModalClassName(
        modalClassName.replaceAll(`modal-bg-blurred`, ``) + " modal-bg-blurred",
      );
    } else {
      setModalClassName(modalClassName.replaceAll(` modal-bg-blurred`, ``));
    }
  };

  const onDeleteButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const find = rowData?.find((x) => x.id === selectedId);
    if (!find || find.length === 0) {
      toast.error(SystemMsg.GridUi.emptySelection);
      return;
    }
    CalendarBackend.deleteCalendar(
      selectedId,
      (res) => {
        if (res.status !== 200) {
          toast.error(SystemMsg.Calendar.deleteFailed(find.name));
          return;
        }
        toast.success(SystemMsg.Calendar.deleteSuccess(find.name));
        gridRef?.current?.api.applyTransaction({ remove: [find] });
        const findIdx = rowData?.indexOf(find);
        if (!findIdx) {
          return;
        }
        rowData?.splice(findIdx, 1);
        getRowData(false);
      },
      (err) => {
        toast.error(SystemMsg.Calendar.deleteFailed(find.name));
        return;
      },
    );
  };

  const topLeftElement = (
    <SearchInput searchValue={keyword} setSearchValueFunc={setKeyword} />
  );

  const topMenuElement = (
    <CreateEditDeleteButtonGroup
      onCreateButtonClick={onCreateButtonClick}
      onEditButtonClick={onEditButtonClick}
      onDeleteButtonClick={onDeleteButtonClick}
      hasEdit={true}
      disableEdit={selectedId === -1}
      hasDelete={selectedId !== -1}
    />
  );

  const saveCalendarClick = (boolVal: boolean, data: CalendarViewObject) => {
    setShowModal(false);
    setModalDataObj(null);
    gridRef.current?.api?.deselectAll();
    if (!boolVal) {
      return;
    }
    if (crudMode === "add") {
      addCalendarCallback();
    } else if (crudMode === "edit") {
      editCalendarCallback(data);
    }
  };

  const addCalendarCallback = () => {
    getRowData();
  };

  const editCalendarCallback = (data: CalendarViewObject) => {
    if (!data || !rowData) {
      toast.error(SystemMsg.GridUi.emptyData);
      return;
    }
    const obj = data as CalendarViewObject;
    if (obj.id && obj.id.toString().length > 0) {
      obj.id = Number(obj.id);
    }
    const find = rowData?.find((x) => x.id.toString() === obj.id.toString());
    if (!find) {
      toast.error(SystemMsg.GridUi.failedToFindRowById(obj.id));
      return;
    }

    const findIdx = rowData?.indexOf(find);
    if (!find || findIdx === undefined || findIdx < 0) {
      toast.error(SystemMsg.GridUi.doesNotIncludeRecord(obj.id));
      return;
    }
    const tempArray = [obj];
    rowData[findIdx] = tempArray[0];
    const rowNode = gridRef?.current?.api?.getRowNode(findIdx.toString());
    rowNode?.setData(tempArray[0]);
    getRowData(false);
  };

  return (
    <>
      <Modal
        size="lg"
        show={show}
        onHide={handleClose}
        backdrop="static"
        dialogClassName={modalClassName}
        centered
      >
        <Modal.Header
          style={{ border: "none", paddingBottom: "4px" }}
          closeButton
        >
          <Modal.Title>
            <ModalIconTitle
              title={title}
              iconElement={<MdOutlineCalendarMonth />}
            />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Datagrid
            rowSelect="singleRow"
            height="60vh"
            gridRef={gridRef}
            rowData={rowData}
            colDefs={colDefs}
            onSelectionChanged={onSelectionChanged}
            hasPagination
            handlePageClick={handlePageClick}
            handlePageSizeChange={handlePageSizeChange}
            paginatePage={paginationPage}
            paginatePageCount={pageCount}
            topLeftElement={topLeftElement}
            topRightElement={topMenuElement}
            loading={gridLoading}
          />
        </Modal.Body>
      </Modal>
      <CalendarModal
        toast={toast}
        mode={crudMode}
        show={showModal}
        onSaveBtnClick={saveCalendarClick}
        onClose={() => {
          setShowModal(false);
          setModalDataObj(null);
        }}
        data={modalDataObj}
      />
    </>
  );
}

export default CalendarListModal;
